import { useState, useRef, useEffect } from 'react'

const API_BASE  = (process.env.REACT_APP_API_URL || '')
const TOKEN_KEY = 'pnl_chat_token'

function getToken() { return localStorage.getItem(TOKEN_KEY) }

async function apiFetch(path, options = {}) {
  const token = getToken()
  const headers = { ...(options.headers || {}) }
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(API_BASE + path, { ...options, headers })
  const text = await res.text()
  let data
  try { data = JSON.parse(text) } catch { data = text }
  if (!res.ok) throw new Error(data?.error || data?.detail || res.statusText)
  return data
}

const YEAR_COLORS = { '2023':'#F59E0B','2024':'#10B981','2025':'#6366F1','2026':'#EF4444' }
const yearAccent = (p) => YEAR_COLORS[p?.split('-')[0]] ?? '#6366F1'

export default function UploadPage() {
  const [dragging, setDragging]     = useState(false)
  const [file, setFile]             = useState(null)
  const [status, setStatus]         = useState('idle')
  const [message, setMessage]       = useState('')
  const [conflict, setConflict]     = useState(null)
  const [periods, setPeriods]       = useState([])
  const [lastResult, setLastResult] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const inputRef = useRef(null)

  const loadPeriods = async () => {
    try {
      const data = await apiFetch('/api/data/periods')
      setPeriods(data.periods || [])
    } catch(e) { console.log('Load periods error:', e.message) }
  }

  useEffect(() => { loadPeriods() }, [])

  const selectFile = (f) => {
    if (!f.name.endsWith('.xlsx') && !f.name.endsWith('.xls')) {
      setStatus('error')
      setMessage('Only .xlsx / .xls files are supported.')
      return
    }
    setFile(f); setStatus('idle'); setMessage(''); setConflict(null)
  }

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false)
    const f = e.dataTransfer.files[0]
    if (f) selectFile(f)
  }

  const doUpload = async (endpoint) => {
    if (!file) return
    setStatus('uploading')
    const form = new FormData()
    form.append('file', file)
    try {
      const data = await apiFetch(endpoint, { method: 'POST', body: form })
      if (data.status === 'conflict') {
        setConflict(data); setStatus('conflict')
      } else {
        setLastResult(data); setStatus('success'); setFile(null); loadPeriods()
      }
    } catch(e) {
      setStatus('error'); setMessage(e.message || 'Upload failed.')
    }
  }

  const handleDelete = async (period) => {
    try {
      await apiFetch(`/api/upload/${period}`, { method: 'DELETE' })
      setDeleteConfirm(null)
      loadPeriods()
    } catch(e) {
      setStatus('error'); setMessage(e.message || 'Delete failed.')
    }
  }

  const FONT = '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif'

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT, minHeight: '100vh', background: '#f8fafc' }}>

      {/* Header */}
      <div style={{ marginBottom: 28, paddingBottom: 20, borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: 'linear-gradient(135deg,#f97316,#ea580c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📤</div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', letterSpacing: -0.5 }}>Upload P&L Data</div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>Admin only — Upload Excel files to update financial data</div>
          </div>
        </div>
      </div>

      {/* Two column layout */}
      <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start' }}>

        {/* Left — Drop zone */}
        <div style={{ width: 380, flexShrink: 0 }}>

          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            style={{
              border: `2px dashed ${dragging ? '#f97316' : file ? '#22c55e' : '#e2e8f0'}`,
              borderRadius: 16,
              padding: '40px 24px',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragging ? '#fff7ed' : file ? '#f0fdf4' : '#fff',
              transition: 'all 0.2s',
              marginBottom: 16,
            }}
          >
            <input ref={inputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
              onChange={e => e.target.files?.[0] && selectFile(e.target.files[0])} />
            <div style={{ fontSize: 48, marginBottom: 12 }}>
              {file ? '📄' : '📊'}
            </div>
            {file ? (
              <>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#16a34a', marginBottom: 4 }}>{file.name}</div>
                <div style={{ fontSize: 12, color: '#86efac' }}>{(file.size / 1024).toFixed(1)} KB · Ready to upload</div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 6 }}>Drop Excel file here</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>or click to browse · .xlsx only · max 20MB</div>
                <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 8 }}>Expected: YYYY.MM PL Analysis TSI Consol_V1.xlsx</div>
              </>
            )}
          </div>

          {/* Upload button */}
          {file && status !== 'conflict' && (
            <button
              onClick={() => doUpload('/api/upload')}
              disabled={status === 'uploading'}
              style={{
                width: '100%', padding: '13px 0', borderRadius: 10, border: 'none',
                background: status === 'uploading' ? '#e2e8f0' : 'linear-gradient(135deg,#f97316,#ea580c)',
                color: status === 'uploading' ? '#94a3b8' : '#fff',
                fontSize: 15, fontWeight: 700, cursor: status === 'uploading' ? 'not-allowed' : 'pointer',
                boxShadow: status === 'uploading' ? 'none' : '0 4px 14px rgba(249,115,22,0.35)',
                transition: 'all 0.2s', marginBottom: 12,
              }}>
              {status === 'uploading' ? '⏳ Processing...' : '🚀 Upload & Process'}
            </button>
          )}

          {/* Clear button */}
          {file && (
            <button onClick={() => { setFile(null); setStatus('idle'); setMessage(''); setConflict(null) }}
              style={{ width: '100%', padding: '10px 0', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              ✕ Clear
            </button>
          )}

          {/* Conflict */}
          {status === 'conflict' && conflict?.existing && (
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '16px', marginTop: 12 }}>
              <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 6 }}>⚠️ {conflict.period} already loaded</div>
              <div style={{ fontSize: 12, color: '#78350f', marginBottom: 12 }}>
                {conflict.existing.row_count} rows · {new Date(conflict.existing.uploaded_at).toLocaleDateString()}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setStatus('idle'); setConflict(null) }}
                  style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={() => doUpload('/api/upload/replace')}
                  style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#f97316', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  Replace {conflict.period}
                </button>
              </div>
            </div>
          )}

          {/* Success */}
          {status === 'success' && lastResult && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12, padding: '16px', marginTop: 12 }}>
              <div style={{ fontWeight: 700, color: '#16a34a', marginBottom: 4 }}>🎉 {lastResult.period} loaded!</div>
              <div style={{ fontSize: 13, color: '#15803d' }}>
                {lastResult.rows_added} rows added
                {lastResult.rows_removed ? `, ${lastResult.rows_removed} replaced` : ''}
              </div>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '16px', marginTop: 12 }}>
              <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: 4 }}>❌ Upload failed</div>
              <div style={{ fontSize: 13, color: '#b91c1c' }}>{message}</div>
            </div>
          )}

          {/* File format help */}
          <div style={{ marginTop: 20, padding: '16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 8 }}>📋 File Format Required</div>
            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.7 }}>
              <div>• Filename: <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>YYYY.MM PL Analysis TSI Consol_V1.xlsx</code></div>
              <div>• Sheet name: <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>TSI</code></div>
              <div>• Max size: 20MB</div>
            </div>
          </div>
        </div>

        {/* Right — Loaded periods */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>Loaded Periods</div>
            {periods.length > 0 && (
              <div style={{ fontSize: 12, color: '#94a3b8' }}>
                {periods.reduce((s, p) => s + (p.row_count || 0), 0).toLocaleString()} total rows · {periods.length} period{periods.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          {periods.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🌱</div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>No data loaded yet</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Drop your first Excel file on the left</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {[...periods].sort((a,b) => b.period.localeCompare(a.period)).map(p => (
                <div key={p.period} style={{
                  background: '#fff', border: '1px solid #e2e8f0',
                  borderRadius: 12, padding: '14px 16px',
                  display: 'flex', alignItems: 'center', gap: 12,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                  transition: 'all 0.18s', position: 'relative', overflow: 'hidden',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.1)'; e.currentTarget.style.transform='translateY(-2px)' }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.transform='none' }}>
                  {/* Year color strip */}
                  <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: yearAccent(p.period), borderRadius: '12px 0 0 12px' }} />

                  <div style={{ flex: 1, minWidth: 0, paddingLeft: 4 }}>
                    <div style={{ fontWeight: 800, fontSize: 15, color: '#1e293b', letterSpacing: -0.3 }}>{p.period}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, display: 'flex', gap: 6 }}>
                      <span>{(p.row_count || 0).toLocaleString()} rows</span>
                      <span>·</span>
                      <span>{new Date(p.uploaded_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'2-digit' })}</span>
                    </div>
                  </div>

                  {deleteConfirm === p.period ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                      <button onClick={() => handleDelete(p.period)}
                        style={{ padding: '3px 8px', borderRadius: 6, border: 'none', background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✓ Yes</button>
                      <button onClick={() => setDeleteConfirm(null)}
                        style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 11, cursor: 'pointer' }}>✕ No</button>
                    </div>
                  ) : (
                    <button onClick={() => setDeleteConfirm(p.period)}
                      style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 6, border: '1px solid #e2e8f0', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background='#fef2f2'; e.currentTarget.style.color='#ef4444'; e.currentTarget.style.borderColor='#fecaca' }}
                      onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#94a3b8'; e.currentTarget.style.borderColor='#e2e8f0' }}
                      title={`Delete ${p.period}`}>🗑</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}